import type { ObjectDirective } from 'vue';

import { useServiceContainer } from '~/container';

interface InfiniteScrollPayload {
    disabled?: boolean;
    callback: Function;
}

interface InfiniteScrollReference {
    bindings: InfiniteScrollPayload;
    handler: () => void;
}

// References
const onScrollReference = new Map<HTMLElement, InfiniteScrollReference>();

export const InfiniteScroll: ObjectDirective<HTMLImageElement, InfiniteScrollPayload> = {
    mounted(el, binding): void {
        const { ElementHelperService } = useServiceContainer();

        // Set references
        onScrollReference.set(el, {
            bindings: binding.value,
            handler,
        });

        // Set listeners
        window.addEventListener('scroll', onScrollReference.get(el)!.handler);

        function handler() {
            const currentReference = onScrollReference.get(el);

            if (!currentReference) {
                return;
            }

            const { disabled, callback } = currentReference.bindings;

            if (disabled) {
                return;
            }

            const windowBottom = window.innerHeight + window.scrollY;
            const elementBottom = ElementHelperService.getOffsetTop(el) + ElementHelperService.height(el);
            const remaining = elementBottom - windowBottom;
            const shouldScroll = remaining <= window.innerHeight;

            if (shouldScroll) {
                return callback();
            }
        }
    },
    updated(el, binding) {
        const currentReference = onScrollReference.get(el);

        if (!currentReference) {
            return;
        }

        // Update reference with new bindings
        onScrollReference.set(el, {
            ...currentReference,
            bindings: binding.value,
        });
    },
    beforeUnmount(el) {
        const currentReference = onScrollReference.get(el);

        if (!currentReference) {
            return;
        }

        // Remove listeners
        window.removeEventListener('scroll', currentReference.handler);

        // Remove references to clear potential memory leaks
        onScrollReference.delete(el);
    },
};
